/* ===========================================================
   sync/notes.js — Notes Protocol's small notes, offline-first

   Every note lives on this device (localStorage, per account) and
   changes show at once. Changed notes wait in a small send list and go
   to the server (worker/notes.js) when online; the newest change wins.
   =========================================================== */

import { signal } from '@preact/signals';
import { api, isOffline } from '../lib/api.js';
import { session, online } from '../state.js';
import { newId } from '../../shared/protocol.js';

const key = s => `dp:notes:${session.value.user?.id || 'x'}:${s}`;
const read = (s, d) => { try { return JSON.parse(localStorage.getItem(key(s))) ?? d; } catch { return d; } };
const write = (s, v) => { try { localStorage.setItem(key(s), JSON.stringify(v)); } catch { /* full or private */ } };

export const notes = signal([]);
let outbox = new Set();
let loadedFor = null;

const sortNotes = list => list.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

function load() {
  const who = session.value.user?.id || 'x';
  if (loadedFor === who) return;
  loadedFor = who;
  notes.value = sortNotes(read('list', []));
  outbox = new Set(read('out', []));
}

function persist() {
  write('list', notes.value);
  write('out', [...outbox]);
}

export const blankNote = (patch = {}) => ({
  id: newId(), title: '', body: '', items: null, color: '', pinned: false, archived: false,
  deletedAt: null, createdAt: Date.now(), updatedAt: Date.now(), ...patch,
});

export const isEmpty = n => !n.title.trim() && !n.body.trim() && !(n.items || []).some(i => i.t.trim());

/** Save a note (new or changed). */
export function saveNote(n) {
  load();
  const next = { ...n, updatedAt: Date.now() };
  const rest = notes.value.filter(x => x.id !== n.id);
  notes.value = sortNotes([next, ...rest]);
  outbox.add(n.id);
  persist();
  sendSoon();
  return next;
}

export const patchNote = (id, patch) => {
  const n = notes.value.find(x => x.id === id);
  return n && saveNote({ ...n, ...patch });
};

/** Gone for good (from the trash). */
export async function deleteNoteForever(id) {
  notes.value = notes.value.filter(x => x.id !== id);
  outbox.delete(id);
  persist();
  try { await api(`/api/notes/${id}`, { method: 'DELETE' }); } catch { /* the 7-day cleanup gets it */ }
}

let timer = null, sending = null;
function sendSoon() { clearTimeout(timer); timer = setTimeout(sendNotes, 700); }

export function sendNotes() {
  if (sending) return sending;
  sending = (async () => {
    load();
    if (!online.value) return;
    for (const id of [...outbox]) {
      const n = notes.value.find(x => x.id === id);
      if (!n) { outbox.delete(id); continue; }
      try {
        await api(`/api/notes/${id}`, { method: 'PUT', body: n });
        if (notes.value.find(x => x.id === id)?.updatedAt === n.updatedAt) outbox.delete(id);
      } catch (err) {
        if (isOffline(err) || err.status >= 500 || err.status === 429) break;   // later
        outbox.delete(id);   // the server said no for good
      }
    }
    persist();
  })().finally(() => { sending = null; });
  return sending;
}

/** Bring this device up to date with the server. */
export async function refreshNotes() {
  load();
  await sendNotes();
  let server;
  try { server = (await api('/api/notes')).notes; } catch (err) { if (!isOffline(err)) console.warn('[notes]', err.message); return; }
  const mine = new Map(notes.value.map(n => [n.id, n]));
  const list = [];
  for (const s of server) {
    const m = mine.get(s.id);
    list.push(m && outbox.has(s.id) && m.updatedAt >= s.updatedAt ? m : s);
    mine.delete(s.id);
  }
  for (const [id, m] of mine) if (outbox.has(id)) list.push(m);   // made here, not sent yet
  notes.value = sortNotes(list);
  persist();
}

export function useNotesBoot() {
  load();
  return notes.value;
}
